"use client";

import React from "react";
import {Box, Typography, Stack, Tooltip} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Image from "next/image";
import { TourInclusion } from "@/types/dto/pdp/pdp-dates-inclusions-pricing";
import {useTranslations} from "next-intl";

// Map inclusion titles to local icon paths
const INCLUSION_ICON_MAP: Record<string, string> = {
  "On-tour air included on this tour": "/images/inclusions/on-tour-air.svg",
  "Money-back travel protection plan": "/images/inclusions/travel-protection.svg",
  "Roundtrip home-to-airport Private Chauffeur Service": "/images/inclusions/private-chauffeur.svg",
  "Tours designed and led by locals": "/images/inclusions/expertly-led.svg",
  "Many guaranteed departures available": "/images/inclusions/guaranteed-departures.svg"
};

// Helper to get icon URL - prefer local icons, fall back to API URL
const getIconUrl = (inclusion: TourInclusion): string | undefined => {
  // Check for local icon first
  const localIcon = INCLUSION_ICON_MAP[inclusion.title];
  if (localIcon) {
    return localIcon;
  }

  // Fall back to API icon URL
  if (inclusion.iconUrl) {
    return inclusion.iconUrl.startsWith("//") ? `https:${inclusion.iconUrl}` : inclusion.iconUrl;
  }

  return undefined;
};

interface InclusionItemProps {
  inclusion: TourInclusion;
  index: number;
  onClick?: () => void;
}

const InclusionItem = ({ inclusion, index, onClick }: InclusionItemProps) => {
  const iconUrl = getIconUrl(inclusion);
  const t = useTranslations("PDP");

  return (
    <Tooltip title={inclusion.tooltipText && t(inclusion.tooltipText)}>
      <Stack
        tabIndex={onClick ? 0 : -1}
        direction="row"
        spacing={1.2}
        alignItems="center"
        onClick={onClick}
        sx={{
          cursor: onClick ? "pointer" : "default",
          "&:focus-visible": {
            outline: "solid 1px",
            outlineColor: "primary.main",
            outlineOffset: "1px"
          }
        }}
        data-testid="inclusion-item"
      >

        {/* Icon */}
        {iconUrl && (
          <Box
            sx={{
              width: "2.8rem",
              height: "2.8rem",
              flexShrink: 0,
              position: "relative"
            }}
          >
            <Image
              src={iconUrl}
              alt=""
              fill
              style={{ objectFit: "contain" }}
              data-testid={`inclusions-item-image_${index}`}
            />
          </Box>
        )}

        {/* Title */}
        <Typography
          sx={{
            fontSize: "1.6rem",
            fontWeight: 500,
            color: "text.primary"
          }}
          data-testid={`inclusions-item-title_${index}`}
        >
          {t(inclusion.title)}
        </Typography>

        {/* Info Icon for Tooltip */}
        {inclusion.tooltipText && (
          <InfoOutlinedIcon
            data-testid="info-icon"
            color="primary"
            sx={{
              width: "2rem",
              height: "2rem"
            }}
          />
        )}
      </Stack>
    </Tooltip>
  );
};

export default InclusionItem;
